import { ExternalLink, Inbox } from 'lucide-react';
import { Badge } from '../ui/Badge';
import { EmptyState } from '../ui/EmptyState';
import { formatRelativeTime, priorityColor } from '../../utils/format';
import type { JiraIssue } from '../../types';
import { cn } from '../../utils/cn';

const statusTone: Record<JiraIssue['statusCategory'], 'neutral' | 'warning' | 'success'> = {
  new: 'neutral',
  indeterminate: 'warning',
  done: 'success',
};

export function IssueTable({ issues, loading }: { issues: JiraIssue[]; loading?: boolean }) {
  if (!loading && issues.length === 0) {
    return (
      <EmptyState
        icon={Inbox}
        title="No issues match this view"
        description="Try a different project, type, status, or search term."
      />
    );
  }

  return (
    <div className="overflow-x-auto scrollbar-thin">
      <table className="w-full min-w-[720px] text-left text-sm">
        <thead>
          <tr className="border-b border-slate-100 text-xs uppercase tracking-wide text-slate-400 dark:border-slate-800">
            <th className="py-2.5 pr-4 font-medium">Issue</th>
            <th className="py-2.5 pr-4 font-medium">Type</th>
            <th className="py-2.5 pr-4 font-medium">Status</th>
            <th className="py-2.5 pr-4 font-medium">Priority</th>
            <th className="py-2.5 pr-4 font-medium">Assignee</th>
            <th className="py-2.5 pr-4 font-medium">Updated</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
          {loading
            ? Array.from({ length: 6 }).map((_, i) => (
                <tr key={i}>
                  <td colSpan={6} className="py-3">
                    <div className="h-4 w-full animate-pulse rounded bg-slate-100 dark:bg-slate-800" />
                  </td>
                </tr>
              ))
            : issues.map((issue) => (
                <tr key={issue.id} className="group hover:bg-slate-50 dark:hover:bg-slate-800/50">
                  <td className="py-3 pr-4">
                    <a
                      href={issue.url}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-1.5 font-medium text-slate-800 hover:text-pulse-600 dark:text-slate-200 dark:hover:text-pulse-400"
                    >
                      <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                        {issue.key}
                      </span>
                      <span className="max-w-xs truncate">{issue.summary}</span>
                      <ExternalLink size={12} className="shrink-0 opacity-0 transition-opacity group-hover:opacity-60" />
                    </a>
                  </td>
                  <td className="py-3 pr-4 text-slate-500 dark:text-slate-400">{issue.issueType}</td>
                  <td className="py-3 pr-4">
                    <Badge tone={statusTone[issue.statusCategory]}>{issue.status}</Badge>
                  </td>
                  <td className="py-3 pr-4">
                    <span className="inline-flex items-center gap-1.5 text-slate-600 dark:text-slate-300">
                      <span className={cn('h-2 w-2 rounded-full', priorityColor[issue.priority] ?? 'bg-slate-300')} />
                      {issue.priority}
                    </span>
                  </td>
                  <td className="py-3 pr-4 text-slate-600 dark:text-slate-300">
                    {issue.assignee?.displayName ?? 'Unassigned'}
                  </td>
                  <td className="py-3 pr-4 text-slate-400">{formatRelativeTime(issue.updated)}</td>
                </tr>
              ))}
        </tbody>
      </table>
    </div>
  );
}